import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { Monitor, CreditCard, Smartphone, Tv, Globe, Loader2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { PageHero, Section, Container, Eyebrow, H2, Lead, CTAButton, GlassCard, Orbs } from "../components/PageUI";
import { useToast } from "../hooks/use-toast";
import SEO, { SITE_URL } from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://masjid-suite-backend.onrender.com";

const hardwareBlocks = [
  { id: "kiosk", name: '27" Digital Donation Kiosk', image: "/assets/kiosk.png", desc: "Large touchscreen kiosk for your lobby or entrance. Accepts cards, tap, and mobile wallets.", icon: Monitor, link: "/kiosk", linkText: "See full specs →", oneTime: 999, monthly: 89 },
  { id: "pos", name: "Mobile POS", image: "/assets/pos.png", fill: true, desc: "Handheld card reader for taking donations anywhere in the masjid.", icon: Smartphone, link: "/mobile-pos", linkText: "See full specs →", oneTime: 399, monthly: 39 },
  { id: "signage", name: "Signage Player", image: "/assets/signage.png", desc: "Plug-and-play device for prayer times and announcements on your screens.", icon: Tv, oneTime: 199, monthly: 19 },
];

const productJsonLd = hardwareBlocks.map((h) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: h.name,
  description: h.desc,
  image: `${SITE_URL}${h.image}`,
  brand: { "@type": "Brand", name: "Masjid Suite" },
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "USD",
    lowPrice: h.monthly,
    highPrice: h.oneTime,
    offerCount: 2,
    offers: [
      { "@type": "Offer", name: "One-time purchase", price: h.oneTime, priceCurrency: "USD", availability: "https://schema.org/InStock" },
      { "@type": "Offer", name: "Monthly rental", price: h.monthly, priceCurrency: "USD", availability: "https://schema.org/InStock" },
    ],
  },
}));

const rates = [
  ["Online card payments", "2.9% + $0.30 per transaction"],
  ["In-person (Kiosk, Mobile POS)", "2.7% + $0.05 per transaction"],
  ["ACH bank transfer", "0.8% per transaction, capped at $5"],
  ["International cards", "+1.5%"],
  ["Currency conversion", "+1%"],
  ["Disputed charges", "$15 per dispute"],
];

function BuyButtons({ product }) {
  const [loading, setLoading] = useState(null); // "one_time" | "monthly" | null
  const { toast } = useToast();

  const startCheckout = async (purchaseType) => {
    setLoading(purchaseType);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/checkout/session`, {
        product_id: product.id,
        purchase_type: purchaseType,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast({
        title: "Checkout unavailable",
        description: err?.response?.data?.detail || "Something went wrong starting checkout. Please try again or contact us.",
        variant: "destructive",
      });
      setLoading(null);
    }
  };

  return (
    <div className="mt-6 flex flex-wrap gap-3">
      <button
        onClick={() => startCheckout("one_time")}
        disabled={loading !== null}
        className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium btn-primary disabled:opacity-70"
      >
        {loading === "one_time" ? <Loader2 size={16} className="animate-spin" /> : null}
        Buy Outright - ${product.oneTime}
      </button>
      <button
        onClick={() => startCheckout("monthly")}
        disabled={loading !== null}
        className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium btn-gold disabled:opacity-70"
      >
        {loading === "monthly" ? <Loader2 size={16} className="animate-spin" /> : null}
        Rent - ${product.monthly}/mo
      </button>
      {product.link && (
        <Link to={product.link} className="inline-flex items-center gap-1 text-[#0e2408] font-medium hover:gap-2 transition-all self-center">
          {product.linkText}
        </Link>
      )}
    </div>
  );
}

export default function Pricing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    const sessionId = searchParams.get("session_id");
    if (!checkout) return;

    if (checkout === "success" && sessionId) {
      axios
        .get(`${BACKEND_URL}/api/checkout/session/${sessionId}/status`)
        .then((res) => {
          const paid = res.data.payment_status === "paid";
          toast({
            title: paid ? "Payment received!" : "Almost there",
            description: paid
              ? "Thanks for your order. We'll be in touch to arrange delivery/setup."
              : "We're confirming your payment with Stripe. This can take a moment.",
          });
        })
        .catch(() => {
          toast({ title: "Payment received", description: "We're confirming the details now." });
        });
    } else if (checkout === "cancelled") {
      toast({ title: "Checkout cancelled", description: "No charge was made. Feel free to try again anytime." });
    }

    searchParams.delete("checkout");
    searchParams.delete("session_id");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <SEO
        title="Hardware & Pricing | Masjid Suite"
        description="Buy or rent the Masjid Suite donation kiosk, Mobile POS, and Signage Player. Core software stays free forever, with Stripe's standard processing rates and no markup."
        path="/pricing"
        jsonLd={productJsonLd}
      />
      <PageHero
        eyebrow="Hardware & Pricing"
        title="Simple pricing."
        gradient="Never a monthly fee for software."
        lead={<>All core software is free, always. Below is every device we sell, and you can buy it outright or rent it month to month, whichever fits your masjid’s budget. No bundles to decode.</>}
      />

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="software stays free.">Hardware. Either way,</H2>
            <div className="mt-6 flex justify-center"><Lead>Buy outright, or rent month to month with no long-term commitment.</Lead></div>
          </div>

          <div className="space-y-8">
            {hardwareBlocks.map((h) => {
              const Icon = h.icon;
              return (
                <GlassCard key={h.id} hover={false}>
                  <div className="grid md:grid-cols-3 gap-8 items-center">
                    <div className={`${h.fill ? "aspect-[900/1426]" : "aspect-square"} rounded-2xl border border-[#e6e7eb] overflow-hidden ${h.fill ? "" : "bg-white flex items-center justify-center p-4"}`}>
                      {h.image ? (
                        h.fill ? (
                          <img src={h.image} alt={h.name} className="w-full h-full object-cover" draggable={false} />
                        ) : (
                          <img src={h.image} alt={h.name} className="max-w-full max-h-full object-contain" draggable={false} style={{ filter: "drop-shadow(0 12px 20px rgba(14,36,8,0.15))" }} />
                        )
                      ) : (
                        <Icon size={80} strokeWidth={1.4} className="text-[#0e2408]/70" />
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <h3 className="font-display text-2xl md:text-3xl text-[#1d1d1f] font-semibold mb-3">{h.name}</h3>
                      <p className="text-[#4b5563] text-[15px] leading-relaxed">{h.desc}</p>
                      <BuyButtons product={h} />
                    </div>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        </Container>
      </Section>

      <Section id="apps" className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <Eyebrow>Apps</Eyebrow>
            <H2 gradient="free">Always</H2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <GlassCard>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4"><CreditCard size={22}/></div>
              <h3 className="font-display text-xl text-[#1d1d1f] font-semibold mb-2">Tap & Pay App</h3>
              <p className="text-[14px] text-[#4b5563] mb-3">Turn a staff phone into a donation point. Free, always.</p>
              <Link to="/software#apps" className="inline-flex items-center gap-1 text-[#a8862f] text-[13px] font-medium hover:gap-2 transition-all">Learn more →</Link>
            </GlassCard>
            <GlassCard>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#c9a34a] to-[#a8862f] text-white flex items-center justify-center mb-4"><Smartphone size={22}/></div>
              <h3 className="font-display text-xl text-[#1d1d1f] font-semibold mb-2">Masjid Suite – Member App</h3>
              <p className="text-[14px] text-[#4b5563] mb-3">Your masjid’s own branded community app. Free, always.</p>
              <Link to="/member-app" className="inline-flex items-center gap-1 text-[#0e2408] text-[13px] font-medium hover:gap-2 transition-all">Learn more →</Link>
            </GlassCard>
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="grid lg:grid-cols-2 gap-8">
            <GlassCard hover={false}>
              <Eyebrow>Website design</Eyebrow>
              <h3 className="font-display text-2xl md:text-3xl text-[#1d1d1f] font-semibold mb-4">Masjid Website Design</h3>
              <p className="text-[#4b5563] leading-relaxed">A professional masjid website, designed and launched for you, connected to your free Masjid Suite software from day one.</p>
              <div className="mt-6"><CTAButton to="/contact">Talk to Us</CTAButton></div>
            </GlassCard>
            <GlassCard hover={false}>
              <Eyebrow>Payment processing rates</Eyebrow>
              <p className="text-[13px] text-[#4b5563] mb-4">We use Stripe to process every donation, and pass their standard rates straight through with no markup of our own:</p>
              <div className="divide-y divide-emerald-100">
                {rates.map(([k, v], i) => (
                  <div key={i} className="flex items-center justify-between py-2 text-[13px]">
                    <span className="text-[#4b5563]">{k}</span>
                    <span className="text-[#1d1d1f] font-medium">{v}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[#6b7280] mt-4">Rates shown are Stripe’s published standard pricing and may change on their end. We don’t add anything on top.</p>
            </GlassCard>
          </div>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="glass-frosted rounded-[40px] p-10 md:p-16 text-center shadow-apple-lg">
            <H2 gradient="just have questions first?">Ready to order hardware, or</H2>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <CTAButton to="/contact">Order Hardware</CTAButton>
              <CTAButton to="/contact" variant="secondary">Talk to Us First</CTAButton>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
